import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { api, setTokens, clearTokens, getAccessToken } from '../api/client';

const AuthContext = createContext(null);
const SERVER_KEY = 'vokelle_server';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [license, setLicense] = useState(null); // seçili sunucunun tam lisans nesnesi
  const [servers, setServers] = useState([]); // tüm sunucular (özet)
  const [serverSlots, setServerSlots] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [activeServerKey, setActiveServerKeyState] = useState(() => {
    try { return localStorage.getItem(SERVER_KEY) || null; } catch (_) { return null; }
  });
  const [loading, setLoading] = useState(true);

  const setActiveServer = useCallback((key) => {
    setActiveServerKeyState(key || null);
    try { key ? localStorage.setItem(SERVER_KEY, key) : localStorage.removeItem(SERVER_KEY); } catch (_) {}
  }, []);

  // Sunucu listesini + seçili sunucunun lisansını yükler.
  const loadServers = useCallback(async (preferredKey) => {
    try {
      const { servers: list = [], serverSlots: slots = 0, subscriptionStatus: subStatus = null } = await api.servers();
      setServers(list);
      setServerSlots(slots);
      setSubscriptionStatus(subStatus);

      let key = preferredKey || null;
      try { if (!key) key = localStorage.getItem(SERVER_KEY); } catch (_) {}
      const valid = list.find((s) => s.key === key);
      if (!valid) {
        const first = list.find((s) => s.status === 'active') || list[0];
        key = first ? first.key : null;
      }
      setActiveServer(key);

      const { license: lic } = await api.license(key || undefined);
      setLicense(lic);
      return { servers: list, license: lic };
    } catch (_) {
      setServers([]);
      setLicense(null);
      setServerSlots(0);
      setSubscriptionStatus(null);
      return { servers: [], license: null };
    }
  }, [setActiveServer]);

  const loadMe = useCallback(async () => {
    if (!getAccessToken()) {
      setUser(null); setLicense(null); setServers([]); setLoading(false);
      return;
    }
    try {
      const { user: u } = await api.me();
      setUser(u);
      await loadServers();
    } catch (_) {
      clearTokens();
      setUser(null); setLicense(null); setServers([]);
    } finally {
      setLoading(false);
    }
  }, [loadServers]);

  useEffect(() => { loadMe(); }, [loadMe]);

  // Aktif sunucu değişince o sunucunun lisansını çek.
  const selectServer = useCallback(async (key) => {
    setActiveServer(key);
    try {
      const { license: lic } = await api.license(key || undefined);
      setLicense(lic);
    } catch (_) { setLicense(null); }
  }, [setActiveServer]);

  const login = async (email, password) => {
    const data = await api.login({ email, password });
    setTokens(data);
    setUser(data.user);
    await loadServers();
    return data.user;
  };

  const register = async (payload) => {
    const data = await api.register(payload);
    setTokens(data);
    setUser(data.user);
    setLicense(null); setServers([]); setServerSlots(0);
    return data.user;
  };

  const logout = () => {
    clearTokens();
    setUser(null); setLicense(null); setServers([]); setServerSlots(0);
  };

  const hasActiveLicense = useMemo(() => servers.some((s) => s.status === 'active'), [servers]);
  const isPaused = subscriptionStatus === 'paused';
  const activeServer = useMemo(
    () => servers.find((s) => s.key === activeServerKey) || null,
    [servers, activeServerKey]
  );

  return (
    <AuthContext.Provider
      value={{
        user, setUser, license, servers, serverSlots, activeServerKey, activeServer,
        subscriptionStatus, isPaused,
        hasActiveLicense, loading, login, register, logout,
        selectServer, reload: loadMe, reloadLicense: loadServers, reloadServers: loadServers,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
